import { useState, useRef } from 'react';
import { trackButtonClick } from '../../analytics';

export default function CheckoutPage() {
  const [formData, setFormData] = useState({ first_name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({ first_name: '', email: '' });
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const basePrice = 298.00;
  const discount = 200.00;
  const subtotal = basePrice;
  const dueNow = basePrice - discount;

  const validateForm = () => {
    const newErrors = { first_name: '', email: '' };
    let isValid = true;

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Please fill out this field';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Please fill out this field';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    trackButtonClick('complete_purchase', 'checkout');

    // Submit form to hidden iframe to avoid redirect
    const form = e.target as HTMLFormElement;
    form.submit();

    // Show success message after a short delay
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({ first_name: '', email: '' });
        setErrors({ first_name: '', email: '' });
      }, 3000);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Course Details */}
          <div className="order-1">
            <div className="mb-6">
              <img
                src="https://static.readdy.ai/image/1ee33a74a7d817227244af36e3fd2b0e/bb81daf869b00bf1e657e03b1c8d360f.jpeg"
                alt="AI Creator Course"
                className="w-full rounded"
              />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-6">The Ai Creator Course</h1>

            <div className="text-sm text-gray-700 space-y-4">
              <h3 className="font-bold text-base">
                Lifetime Access for only <span className="line-through">($298)</span> $98
              </h3>

              <hr className="border-gray-300" />

              <h3 className="font-bold">What You Will Learn:</h3>
              <p className="leading-relaxed">
                ✔ The best Ai tools for creators<br />
                ✔ How to train Ai to learn your voice and create viral content ideas<br />
                ✔ How to create incredible images with Ai (all popular styles)<br />
                ✔ How to create websites & graphics with Ai<br />
                ✔ How to create professional logos and brand art with Ai<br />
                ✔ How to create viral youtube thumbnails with Ai<br />
                ✔ How to plan, script, and create viral short form content<br />
                ✔ How to create Ai b-roll and videos<br />
                ✔ How to plan and create powerful ads with Ai<br />
                ✔ How to plan, script and create youtube videos with Ai<br />
                ✔ The best Ai tools for video editors
              </p>

              <hr className="border-gray-300" />

              <p className="text-xs italic leading-relaxed">
                ✔ LIFETIME ACCESS to ALL content and future updates<br />
                ✔ STREAM ANYTIME on computer or mobile, at your own pace.<br />
                ✔ STEP-BY-STEP GUIDE to keep you on the path to success<br />
                ✔ REAL LIFE PRACTICE EXERCISES to develop your skills<br />
                ✔ TIPS & TRICKS to save time and money<br />
                ✔ FREE RESOURCES and Discounts
              </p>

              <div className="text-center my-6">
                <img
                  src="https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/site/8228/images/cd100db-a7bd-e3fb-813f-eedf463747_eLWaGR9tRXaCrbFrZLsu_Satisfaction_Guarantee.png"
                  alt="100% Satisfaction Guarantee"
                  className="w-40 mx-auto"
                />
              </div>

              <h3 className="font-bold text-center">100% Satisfaction Guarantee!</h3>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="order-2">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              {/* Price Header */}
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                <h1 className="text-2xl font-bold text-gray-900">
                  <span className="line-through text-gray-500">${basePrice.toFixed(2)} USD</span>
                </h1>
              </div>

              {/* Price Breakdown */}
              <div className="px-6 py-6">
                <div className="mb-6">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 font-normal text-gray-700">Subtotal:</th>
                        <td className="text-right py-2 text-gray-900 whitespace-nowrap">${subtotal.toFixed(2)} USD</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 font-normal text-gray-700">Discount:</th>
                        <td className="text-right py-2">
                          <span className="text-gray-600 text-xs">${discount.toFixed(2)} USD off</span>
                        </td>
                      </tr>
                      <tr className="border-t-2 border-gray-900">
                        <th className="text-left py-3 font-bold text-gray-900">Due Now:</th>
                        <td className="text-right py-3">
                          <span className="text-xl font-bold text-gray-900 whitespace-nowrap">${dueNow.toFixed(2)} USD</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Success Message */}
                {submitSuccess && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <i className="ri-checkbox-circle-line text-3xl text-green-600 mb-2"></i>
                    <p className="text-green-700 font-medium">Thank you! We'll contact you soon.</p>
                  </div>
                )}

                {/* Form */}
                <form
                  method="post"
                  action="https://systeme.io/embedded/36722165/subscription"
                  target="systeme-iframe"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  {/* Full Name */}
                  <div className="mb-4">
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={(e) => {
                        setFormData({ ...formData, first_name: e.target.value });
                        if (errors.first_name) setErrors({ ...errors, first_name: '' });
                      }}
                      className={`w-full px-3 py-2 border ${errors.first_name ? 'border-red-500' : 'border-gray-300'} rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500`}
                      placeholder="Enter your full name"
                    />
                    {errors.first_name && (
                      <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (errors.email) setErrors({ ...errors, email: '' });
                      }}
                      className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500`}
                      placeholder="Enter your email address"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-green-600 text-white py-3 rounded font-bold text-base hover:bg-green-700 transition-colors shadow-md whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Processing...' : 'Complete my purchase'}
                  </button>
                </form>

                {/* Hidden iframe for form submission */}
                <iframe
                  name="systeme-iframe"
                  ref={iframeRef}
                  style={{ display: 'none' }}
                  title="Systeme.io Form Target"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
